// Implements: F-IDN-027
/**
 * The members of one team (v3 `vTeam`): each person with their role in the team (leader or member)
 * and "Quitar", and under the table the row that adds somebody of the organization who is not in
 * the team yet. The add row is left out when there is nobody to offer (or the reader may not list
 * the organization's people).
 */

import { useId, useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { Select } from "../../../shared/ui/atoms/Select";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import type { UserSummary } from "../../../types/api";
import type { TeamRole, TeamView } from "../../../types/organization";
import { DEFAULT_TEAM_ROLE, TEAM_ROLES, asRole, teamRoleLabelKey } from "../model/roles";
import styles from "./Organization.module.css";
import { PersonCell } from "./PersonCell";

export interface TeamMembersTableProps {
  readonly team: TeamView;
  /** The people who may be added; `null` when the reader may not list them. */
  readonly candidates: readonly UserSummary[] | null;
  readonly busy: boolean;
  readonly onRoleChange: (userId: string, role: TeamRole) => void;
  readonly onRemove: (userId: string) => void;
  readonly onAdd: (userId: string, role: TeamRole) => void;
}

function AddRow({
  candidates,
  busy,
  onAdd,
}: {
  readonly candidates: readonly UserSummary[];
  readonly busy: boolean;
  readonly onAdd: (userId: string, role: TeamRole) => void;
}): ReactElement {
  const { t } = useTranslation();
  const base = useId();
  const [picked, setPicked] = useState<string | null>(null);
  const [role, setRole] = useState<TeamRole>(DEFAULT_TEAM_ROLE);
  const who = candidates.some((user) => user.id === picked) ? (picked ?? "") : (candidates[0]?.id ?? "");

  return (
    <div className={styles.addRow}>
      <label className={styles.label} htmlFor={`${base}-who`}>
        {t("organization.team.add.person")}
      </label>
      <Select
        id={`${base}-who`}
        value={who}
        options={candidates.map((user) => ({
          value: user.id,
          label: t("organization.team.add.option", { name: user.full_name, email: user.email }),
        }))}
        onChange={setPicked}
      />
      <Select
        id={`${base}-role`}
        label={t("organization.team.add.role")}
        value={role}
        options={[...TEAM_ROLES].reverse().map((option) => ({ value: option, label: t(teamRoleLabelKey(option)) }))}
        onChange={(value) => {
          const next = asRole(value, TEAM_ROLES);
          if (next !== null) setRole(next);
        }}
      />
      <Button
        variant="secondary"
        loading={busy}
        onClick={() => {
          if (who !== "") onAdd(who, role);
        }}
      >
        <Icon name="user-plus" size="sm" />
        {t("organization.team.add.submit")}
      </Button>
    </div>
  );
}

export function TeamMembersTable({ team, candidates, busy, onRoleChange, onRemove, onAdd }: TeamMembersTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <>
      {team.members.length === 0 ? (
        <EmptyState
          headingLevel={3}
          icon="users"
          title={t("organization.team.noMembers.title")}
          body={t("organization.team.noMembers.body")}
        />
      ) : (
        <div className={styles.scroll}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">{t("organization.team.column.person")}</th>
                <th scope="col">{t("organization.team.column.role")}</th>
                <th scope="col">
                  <span className={styles.srOnly}>{t("organization.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {team.members.map((member) => (
                <tr key={member.user.id}>
                  <td>
                    <PersonCell user={member.user} />
                  </td>
                  <td>
                    <Select
                      id={`team-role-${member.user.id}`}
                      label={t("organization.team.roleOf", { name: member.user.full_name, team: team.name })}
                      value={member.role}
                      disabled={busy}
                      options={TEAM_ROLES.map((option) => ({ value: option, label: t(teamRoleLabelKey(option)) }))}
                      onChange={(value) => {
                        const next = asRole(value, TEAM_ROLES);
                        if (next !== null && next !== member.role) onRoleChange(member.user.id, next);
                      }}
                    />
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <Button
                        variant="ghost"
                        label={t("organization.team.removeFor", { name: member.user.full_name })}
                        disabled={busy}
                        onClick={() => {
                          onRemove(member.user.id);
                        }}
                      >
                        {t("organization.team.remove")}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {candidates === null || candidates.length === 0 ? null : <AddRow candidates={candidates} busy={busy} onAdd={onAdd} />}
    </>
  );
}
